import { Card, Pagination, Tabs, Tooltip } from '@pmi/dsm-react';
import { QuestionOutlineIcon } from '@pmi/dsm-react/dist/components/icons';
import Overlay from '../../shared/components/Overlay';
import { SetStateAction, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { toastWithAnalytics } from '../../shared/analytics';
import AdminWelcomeModal from '../../shared/components/AdminWelcomeModal';
import useAccountAlerts from '../../shared/components/AlertGroup/useAccountAlerts';
import DataCard from '../../shared/components/DataCard';
import InviteWizard from '../../shared/components/InviteWizard';
import { buildTrainingHubUrl } from '../../shared/helpers';
import useAdminVisitedPages from '../../shared/hooks/useAdminVisitedPages';
import useGetDeals from '../../shared/hooks/useGetDeals';
import usePermissions from '../../shared/hooks/usePermissions';
import {
  AgreementApiModel,
  AllDeal,
  DealStatus,
} from '../../shared/models/DealModel';
import PERMISSIONS from '../../shared/permissions';
import { accountSelector } from '../../shared/store/shared/accountSlice';
import checkExpiryStatus from '../../shared/utils/checkExpiryStatus';
import formatCurrency from '../../shared/utils/formatCurrency';
import AgreementDetailsTable from './AgreementDetailsTable';
import ExpireWarningModal from './DealTabs/ExpireWarningModal';
import {
  useGetDashboardAccountSummaryTotalsQuery,
  useGetDashboardDealsQuery,
} from './apiSlice';
import styles from './dashboard.module.scss';

const dashboardTabs = [
  {
    label: 'Active',
    value: 0,
  },
  {
    label: 'Expired',
    value: 1,
    disabled: true,
  },
];

interface SummaryTotals {
  spent: number;
  savings: number;
  agreements: number;
  users: number;
  enrollments: number;
}

export default function Dashboard() {
  const { currentAccountId } = useSelector(accountSelector);
  const { hasAccess } = usePermissions();
  const { isAccountAlertFetching, isAccountAlertLoading } = useAccountAlerts({
    dynamicAlerts: true,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [totals, setTotals] = useState<SummaryTotals>({
    agreements: 0,
    enrollments: 0,
    users: 0,
    savings: 0,
    spent: 0,
  });

  const [selectedDashboardTab, setSelectedDashboardTab] = useState(0);
  const tableContainer = useRef(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showUserInviteWizard, setShowUserInviteWizard] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<AgreementApiModel | null>(
    null,
  );
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [agreements, setAgreements] = useState<AgreementApiModel[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [delay, setDelay] = useState(true);
  const localPageSize = Number(localStorage.getItem('pageSize') ?? 0);
  const { setSelectedHomeDeal, selectedHomeDeal } = useGetDeals({
    allDealsEnabled: true,
  });

  const {
    data: accountDealsQueryResult,
    isLoading: loadingAccountDeals,
    isFetching: fetchingAccountDeals,
    isError: accountDealsError,
  } = useGetDashboardDealsQuery(
    {
      accountId: currentAccountId,
      currentPage,
      pageSize,
      status:
        selectedDashboardTab === 0 ? DealStatus.Active : DealStatus.Expired,
    },
    { skip: !currentAccountId },
  );

  const { data: accountSummaryTotals, isLoading: loadingAccountSummaryTotals } =
    useGetDashboardAccountSummaryTotalsQuery(
      { accountId: currentAccountId },
      { skip: !currentAccountId },
    );

  useEffect(() => {
    setDelay(true);

    let timeout: ReturnType<typeof setTimeout> = null;
    if (!loadingAccountDeals && !fetchingAccountDeals) {
      setAgreements([]);
      if (accountDealsQueryResult) {
        timeout = setTimeout(() => {
          setAgreements(accountDealsQueryResult.items);
          setTotalPages(
            Math.ceil(accountDealsQueryResult.totalItemCount / pageSize),
          );
          setDelay(false);
          document?.getElementById('agreementDetailsHeader')?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'end',
          });
        }, 300);
      }
    }
    return () => clearTimeout(timeout);
  }, [
    accountDealsQueryResult,
    loadingAccountDeals,
    fetchingAccountDeals,
    selectedDashboardTab,
  ]);

  const handleDashboardTabChange = (
    event: any,
    newValue: SetStateAction<number>,
  ) => {
    setAgreements([]);
    setSelectedDashboardTab(newValue);
    setCurrentPage(1);
  };

  const handleAgreementLinkClicked = (deal: AgreementApiModel) => {
    window.scroll({
      top: 0,
      behavior: 'smooth',
    });
    setSelectedHomeDeal(deal);
    setTimeout(() => {
      navigate(`/admin/${currentAccountId}/${deal.id}`);
    }, 100);
  };
  const handleAddPeople = (deal: AgreementApiModel) => {
    const { isExpiring } = checkExpiryStatus(deal.expiration);
    setSelectedDeal(deal);
    if (isExpiring) {
      setShowWarningModal(true);
    } else {
      setShowUserInviteWizard(true);
    }
  };
  const handleCopyHubLink = () => {
    const accountAccessLink = buildTrainingHubUrl(currentAccountId);
    navigator.clipboard.writeText(accountAccessLink);
    document.body.click();
    toastWithAnalytics('Link copied successfully', 'success');
  };
  const handleCopyPointOfContact = (deal: AgreementApiModel) => {
    navigator.clipboard.writeText(
      `${deal.primaryContact.firstName} ${deal.primaryContact.lastName}\n${deal.primaryContact.email}`,
    );
    document.body.click();
    toastWithAnalytics('Primary contact copied successfully', 'success');
  };

  const visit = useAdminVisitedPages('home');

  const productsDataAutoHeight = () => {
    let dealProducts: NodeListOf<any> | null = null;
    if (tableContainer && tableContainer.current) {
      dealProducts =
        tableContainer.current.querySelectorAll('span.deal-product');
    } else {
      dealProducts = document.querySelectorAll(
        `div.${styles.agreementListTableContainer} span.deal-product`,
      );
    }
    if (dealProducts) {
      dealProducts.forEach((d: HTMLElement) => {
        const sku = d?.getAttribute('data-sku');
        const children = tableContainer.current?.querySelectorAll(
          `span.deal-product-value[data-sku="${sku}"]`,
        );
        if (children) {
          children.forEach((c: HTMLElement) => {
            // eslint-disable-next-line no-param-reassign
            c.style.height = `${d.clientHeight}px`;
          });
        }
      });
    }
  };

  const isDisabledActionButton = () => {
    if (selectedDashboardTab === 1) {
      return true;
    }
    return !hasAccess({
      permissions: [PERMISSIONS.ManageDealUsers],
    });
  };
  const noTableRow = () => (
    <p className={`p-3 text-center text-muted ${styles.noRows}`}>
      No {selectedDashboardTab === 0 ? 'active' : 'expired'} agreements to
      display
    </p>
  );

  useEffect(() => {
    if (
      selectedHomeDeal &&
      selectedHomeDeal?.id !== AllDeal.id &&
      location.state?.fromSideNav
    ) {
      navigate(`/admin/${currentAccountId}/${selectedHomeDeal?.id}`, {
        state: { fromSideNav: false },
      });
    } else {
      setSelectedHomeDeal(AllDeal);
    }
  }, [selectedHomeDeal]);

  // set the totals for the summary cards
  useEffect(() => {
    if (!loadingAccountDeals && accountSummaryTotals) {
      const summary: SummaryTotals = {
        spent: accountSummaryTotals.totalSpent,
        savings: accountSummaryTotals.totalSavings,
        users: accountSummaryTotals.totalRegisteredUsers,
        enrollments: accountSummaryTotals.totalEnrollments,
        agreements: accountSummaryTotals.numberOfAgreements,
      };
      setTotals(summary);
    }
  }, [accountSummaryTotals, loadingAccountDeals]);

  useEffect(() => {
    if (!loadingAccountDeals && !visit.isLoading) {
      visit.isFirstVisitThen(() => {
        setShowWelcomeModal(true);
      });
    }
  }, [loadingAccountDeals, loadingAccountSummaryTotals, visit.isLoading]);
  useEffect(() => {
    window.addEventListener('resize', () => {
      productsDataAutoHeight();
    });
    if (localStorage.getItem('pageSize')) {
      const n = Number(localStorage.getItem('pageSize'));
      if (n > 0) {
        setPageSize(n);
      }
    }
    return () => {
      window.removeEventListener('resize', () => {
        productsDataAutoHeight();
      });
    };
  }, []);

  return (
    <div className={styles.dashboard}>
      <div className="d-block pb-3 text-muted">All Agreements</div>
      <div
        className={`${styles.cardBlockContainer} dashboard-card-block d-sm-flex flex-wrap  d-block mb-4`}
      >
        <DataCard
          title={
            <div className="d-flex align-items-center align-content-center">
              <span className="d-block">Total Spent</span>
              <Tooltip
                content="This represents the total amount spent by your company against all agreements on your account (in USD)."
                type="sm"
                className="ms-1 darkTooltip"
                placement="top"
              >
                <QuestionOutlineIcon fontSize="sm" />
              </Tooltip>
            </div>
          }
          total={`$${formatCurrency(totals.spent)}`}
          className={`${styles.cardBlock}`}
        />
        <DataCard
          title={
            <div className="d-flex align-items-center align-content-center">
              <span className="d-block">Total Savings</span>
              <Tooltip
                content="This represents the total amount discounted off products and saved expense by your company against all agreements on your account (in USD)."
                type="sm"
                className="ms-1 darkTooltip"
                placement="top"
              >
                <QuestionOutlineIcon fontSize="sm" />
              </Tooltip>
            </div>
          }
          total={`$${formatCurrency(totals.savings)}`}
          className={` ${styles.cardBlock}`}
        />
        <DataCard
          title={
            <div className="d-flex align-items-center align-content-center">
              <span className="d-block">Number of Agreements</span>
              <Tooltip
                content="This represents how many agreements both active and expired your company has on your account."
                type="sm"
                className="ms-1 darkTooltip"
                placement="top"
              >
                <QuestionOutlineIcon fontSize="sm" />
              </Tooltip>
            </div>
          }
          total={totals.agreements}
          className={`${styles.cardBlock}`}
        />
        <DataCard
          title={
            <div className="d-flex align-items-center align-content-center">
              <span className="d-block">Total Registered Users</span>
              <Tooltip
                content="This represents the total number of unique active Users you have on your account assigned to at least one product."
                type="sm"
                className="ms-1 darkTooltip"
                placement="top"
              >
                <QuestionOutlineIcon fontSize="sm" />
              </Tooltip>
            </div>
          }
          total={totals.users}
          className={`${styles.cardBlock}`}
        />
        <DataCard
          title={
            <div className="d-flex align-items-center align-content-center">
              <span className="d-block">Total Redemptions</span>
              <Tooltip
                content="This represents the total number of product redemptions by your Users on your account."
                type="sm"
                className="ms-1 darkTooltip"
                placement="top"
              >
                <QuestionOutlineIcon fontSize="sm" />
              </Tooltip>
            </div>
          }
          total={totals.enrollments}
          className={`${styles.cardBlock}`}
        />
      </div>
      <div className={styles.dealListContainer}>
        <Card
          className={`${styles.agreementListCard} agreement-list-card-cont p-0`}
        >
          <div className="p-3">
            <h5 id="agreementDetailsHeader">Agreement Details</h5>
            <p className="text-muted mb-0 mt-1">
              Review the details, pricing, and redemption totals of all
              agreements available in this account.
            </p>
          </div>
          <div className={`px-3 ${styles.dashboardTabsContainer}`}>
            <Tabs
              labels={dashboardTabs}
              onChange={handleDashboardTabChange}
              theme="light"
              value={selectedDashboardTab}
              size="sm"
            />
          </div>
          {!loadingAccountDeals &&
            !fetchingAccountDeals &&
            !delay &&
            accountDealsError && (
              <div className="p-3">
                <p>Something went wrong. Please try refreshing your page...</p>
              </div>
            )}
          {delay && (
            <div className="p-3">
              <p>Loading...</p>
            </div>
          )}
          {!loadingAccountDeals && !fetchingAccountDeals && !delay && (
            <>
              <div
                className={`d-block dashboard-alc ${styles.agreementListTableContainer}`}
                ref={tableContainer}
              >
                <AgreementDetailsTable
                  agreements={agreements}
                  noTableRow={noTableRow()}
                  onAddPeople={handleAddPeople}
                  onAgreementLinkClicked={handleAgreementLinkClicked}
                  onCopyHubLink={handleCopyHubLink}
                  onCopyPointOfContact={handleCopyPointOfContact}
                  isDisabledActionButton={isDisabledActionButton}
                />
              </div>
              {totalPages >= 1 && (
                <div className="d-flex justify-content-center align-content-center align-items-center">
                  <Pagination
                    currentPage={currentPage}
                    onChange={(e: unknown, pageNumber: number) => {
                      setCurrentPage(pageNumber);
                    }}
                    onPageSizeChange={(size: number) => {
                      setPageSize(size);
                      localStorage.setItem('pageSize', size.toString());
                      setCurrentPage(1);
                    }}
                    pageSize={localPageSize > 0 ? localPageSize : pageSize}
                    totalPages={totalPages}
                    showPageSizes
                    pageSizes={[
                      { label: '10 per page', value: '10' },
                      { label: '25 per page', value: '25' },
                      { label: '50 per page', value: '50' },
                      { label: '100 per page', value: '100' },
                    ]}
                    disabled={false}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </div>
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={
          isAccountAlertFetching ||
          isAccountAlertLoading ||
          loadingAccountDeals ||
          loadingAccountSummaryTotals ||
          visit.isLoading
        }
      />
      {!isAccountAlertFetching &&
        !isAccountAlertLoading &&
        !loadingAccountDeals &&
        showWelcomeModal && <AdminWelcomeModal />}
      {showWarningModal && (
        <ExpireWarningModal
          deal={selectedDeal}
          onConfirm={() => setShowUserInviteWizard(true)}
          onCancel={() => setShowWarningModal(false)}
        />
      )}
      {showUserInviteWizard && (
        <InviteWizard
          deal={selectedDeal}
          step={selectedDeal ? 'product' : undefined}
          onCancel={() => {
            setShowUserInviteWizard(false);
            setSelectedDeal(null);
          }}
        />
      )}
    </div>
  );
}
